import cx from 'classnames';
import * as React from 'react';
import { FormGroup, IFormGroupProps } from '../FormGroup/FormGroup';
import { HelpBlock } from '../HelpBlock/HelpBlock';
import { memoizeOne } from '../helper/fp';
import { CheckboxControlContext, ICheckboxControlContext } from './CheckboxControlContext';

export interface CheckboxControlGroupProps<Value extends string | number> extends Omit<IFormGroupProps, 'ref'> {
  values?: Value[];
  onChangeValue?: (values: Value[]) => void;
  label?: React.ReactNode;
  /** status of the field */
  status?: ICheckboxControlContext['status'];
  description?: React.ReactNode;
  /** states if the checkbox should be arranged vertically. Default to true */
  vertical?: boolean;
  /** max width of the form group */
  maxWidth?: 'narrow' | 'wide' | 'wide-thin';
  /** tooltip to be displayed when Checkbox is focused */
  tooltip?: React.ReactNode;
  /** error help text for the field that will only be displayed if status is error */
  errorHelpText?: React.ReactNode;
}

/**
 * `CheckboxControlGroup` renders containers for a group of radio input and coordinate between them.
 * `CheckboxControlGroup` and `CheckboxInput` should be used together as Compound Component.
 * As an metaphor, `CheckboxControlGroup` is like `select` element while `CheckboxInput` is like `option` element.
 *
 * If you want to render this wrapper totally differently, instead of add more props to this component, consider create your own wrapper by using `CheckboxControlContext`.
 */
export class CheckboxControlGroup<Value extends string | number> extends React.Component<
  CheckboxControlGroupProps<Value>
> {
  getContextValue = memoizeOne(
    (
      values: CheckboxControlGroupProps<Value>['values'],
      status: ICheckboxControlContext['status'],
      tooltip: ICheckboxControlContext['tooltip']
    ) => ({
      onToggle: this.onToggle,
      status,
      tooltip,
      values: values || [],
    })
  );

  render() {
    const {
      vertical = true,
      required,
      errorHelpText,
      // Context props
      values,
      status,
      tooltip,
      // children props
      children,
      label,
      description,
      onChangeValue,
      ...formGroupProps
    } = this.props;

    return (
      <CheckboxControlContext.Provider
        value={this.getContextValue(values, status, tooltip)}
        // provide value cannot be object literal, else would cause unnecessary rerender
      >
        <fieldset className={cx('tds-CheckboxGroup tds-CheckboxGroup--complex', required && 'is-required')}>
          {label && <legend className="tds-CheckboxGroup-title">{label}</legend>}
          <FormGroup status={status} {...formGroupProps}>
            {description && <p>{description}</p>}
            {errorHelpText && status === 'error' && <HelpBlock>{errorHelpText}</HelpBlock>}
            <div className={cx('input-list', vertical && 'input-list--vertical')}>{children}</div>
          </FormGroup>
          <div />
          {/* Do not remove this line: form-group must not have a bottom margin of 0 when inside a grid */}
        </fieldset>
      </CheckboxControlContext.Provider>
    );
  }

  onToggle = (value: Value) => {
    const { values = [], onChangeValue } = this.props;

    if (onChangeValue) {
      if (values.indexOf(value) === -1) {
        onChangeValue(values.concat(value));
      } else {
        onChangeValue(values.filter((val) => val !== value));
      }
    }
  };
}
